import { GlassTooltip } from "@/components/GlassTooltip";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { PIE_COLORS } from "@/pages/AnalyticsPage.constants";
import * as styles from "@/pages/AnalyticsPage.styles";
import * as shared from "@/styles/shared";
import type { PieLegendChartProps } from "@/types/analytics";
import { BarChart } from "lucide-react";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import * as chartStyles from "./PieLegendChart.styles";

const PieLegendChart = <T extends object>({
  data,
  title,
  noDataLabel,
  noDataHint,
  icon: Icon,
  dataKey,
  nameKey,
  label,
  value,
}: PieLegendChartProps<T>) => {
  const sorted = [...data].sort((a, b) => Number(b[dataKey]) - Number(a[dataKey]));

  return (
    <>
      <CardHeader className={styles.chartCardHeader}>
        <div className={styles.chartHeaderRow}>
          <div className={styles.chartHeaderIcon}>
            <Icon className={shared.iconSm} />
          </div>
          <CardTitle className={styles.chartTitle}>{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {!sorted.length ? (
          <div className={styles.emptyState}>
            <BarChart className={styles.emptyIcon} />
            <p className={styles.noData}>{noDataLabel}</p>
            {noDataHint && <p className={styles.noDataHint}>{noDataHint}</p>}
          </div>
        ) : (
          <div className={chartStyles.chartWrapper}>
            <div className={chartStyles.chartContainer}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sorted}
                    dataKey={dataKey as string}
                    nameKey={nameKey as string}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    label={false}
                  >
                    {sorted.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<GlassTooltip />} />
                  <Legend formatter={() => null} wrapperStyle={{ display: "none" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className={styles.pieLegend}>
              {sorted.map((item, index) => (
                <div key={index} className={styles.pieLegendItem}>
                  <span className={styles.pieLegendDot} style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }} />
                  <span className={styles.pieLegendLabel}>{label(item)}</span>
                  <span className={styles.pieLegendValue}>{value(item)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </>
  );
};

export { PieLegendChart };
